import { ELocalStorageKeys } from '~/features/store/ELocalStorageKeys';
import type { IAuthSDKService } from '../model/IAuthSDKService';
import { useLocalStorage } from '@vueuse/core';
import type { Ref } from 'vue';
import { ErrorBus } from '~/features/error-bus';
import dayjs from 'dayjs';

export class OtpManager {
  private api: IAuthSDKService;

  private phone: Ref<string>;
  private expireAt: Ref<string>;
  private attemptId: Ref<string>;
  private retryAfter: Ref<string>;

  constructor(api: IAuthSDKService) {
    this.phone = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__OTP_SENT_TO_PHONE, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.expireAt = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__OTP_EXPIRES, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.attemptId = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__OTP_ATTEMPT_ID, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.retryAfter = useLocalStorage(ELocalStorageKeys.MM__AUTH_SDK__OTP_RETRY_AFTER, '', {
      listenToStorageChanges: false,
      writeDefaults: false,
    });

    this.api = api;
  }

  public async sendOTP(phone: string, captcha: string) {
    try {
      const response = await this.api.sendOtp({ phone, captcha });
      this.phone.value = phone;
      this.expireAt.value = response.expires;
      this.retryAfter.value = response.retryAfter;
      this.attemptId.value = response.attemptId;
    } catch (error) {
      ErrorBus.emitError({
        e: error as Error,
        emitter: 'AuthSDK.OtpManager.sendOTP',
      });
      throw error;
    }
  }

  public async verifyOtp(otp: string) {
    try {
      const response = await this.api.verifyOtp({
        code: otp,
        phone: this.phone.value,
        attemptId: this.attemptId.value,
      });

      this.clearOtpData();
      return response;
    } catch (error) {
      ErrorBus.emitError({
        e: error as Error,
        emitter: 'AuthSDK.OtpManager.verifyOtp',
      });
      throw error;
    }
  }

  public get sendedOtpData() {
    if (!this.phone.value || !this.expireAt.value || !this.attemptId.value || !this.retryAfter) {
      return null;
    }

    if (this.isOtpExpired()) {
      this.clearOtpData();
      return null;
    }

    return {
      phone: this.phone.value,
      expireAt: this.expireAt.value,
      attemptId: this.attemptId.value,
      retryAfter: this.retryAfter.value,
    };
  }

  private isOtpExpired() {
    const expireAt = dayjs(this.expireAt.value);
    const now = dayjs();
    return expireAt.isBefore(now);
  }

  public clearOtpData() {
    this.phone.value = '';
    this.expireAt.value = '';
    this.retryAfter.value = '';
    this.attemptId.value = '';
  }
}
